// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { Button, Text, TextArea } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type AskV2ThreadMessage } from "./types";
import { type AskV2TransportApi } from "./useAskV2Transport";

type ThreadProps = {
  transport: AskV2TransportApi;
};

function lastUserClientId(messages: AskV2ThreadMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "user") return messages[i]!.clientId;
  }
  return null;
}

export function AskV2Thread({ transport }: ThreadProps) {
  const { t } = useTranslation("ui");
  const editableId = lastUserClientId(transport.messages);

  if (transport.messages.length === 0) return null;

  return (
    <ol
      data-testid="ask-v2-thread"
      className="flex w-full max-w-2xl flex-col gap-3"
    >
      {transport.messages.map((message) => {
        const editing = transport.editingClientId === message.clientId;
        const canEdit =
          message.role === "user" && message.clientId === editableId;

        return (
          <li
            key={message.clientId}
            data-testid={`ask-v2-message-${message.role}`}
            data-status={message.status}
            className={
              message.role === "user"
                ? "ml-auto max-w-[85%] rounded-2xl bg-puntt-neutral-gray-3 px-4 py-3"
                : "mr-auto max-w-[85%] rounded-2xl bg-base-white px-4 py-3 ring-1 ring-puntt-neutral-gray-6"
            }
          >
            {editing ? (
              <div className="flex flex-col gap-2">
                <TextArea
                  data-testid="ask-v2-edit-draft"
                  value={transport.editDraft}
                  onChange={(event) =>
                    transport.setEditDraft(event.target.value)
                  }
                />
                <div className="flex gap-2">
                  <Button
                    size="1"
                    data-testid="ask-v2-save-edit"
                    onClick={() => {
                      void transport.submitEdit();
                    }}
                    disabled={transport.sending || !transport.editDraft.trim()}
                  >
                    {t("askV2.transport.saveEdit")}
                  </Button>
                  <Button
                    size="1"
                    variant="ghost"
                    data-testid="ask-v2-cancel-edit"
                    onClick={transport.cancelEdit}
                  >
                    {t("askV2.transport.cancelEdit")}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Text as="p" size="2">
                  {message.content}
                </Text>
                {message.status === "sending" ? (
                  <Text
                    as="p"
                    size="1"
                    color="gray"
                    data-testid="ask-v2-message-sending"
                  >
                    {t("askV2.transport.sending")}
                  </Text>
                ) : null}
                {message.status === "failed" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <Text as="p" size="1" color="red">
                      {t("askV2.transport.failed")}
                    </Text>
                    <Button
                      size="1"
                      variant="ghost"
                      data-testid="ask-v2-retry"
                      onClick={() => {
                        void transport.retry(message.clientId);
                      }}
                    >
                      {t("askV2.transport.retry")}
                    </Button>
                  </div>
                ) : null}
                {message.status === "aborted" ? (
                  <Text as="p" size="1" color="gray">
                    {t("askV2.transport.stopped")}
                  </Text>
                ) : null}
                {canEdit && !editing ? (
                  <Button
                    size="1"
                    variant="ghost"
                    className="mt-1"
                    data-testid="ask-v2-edit-last"
                    onClick={() => transport.beginEdit(message.clientId)}
                  >
                    {t("askV2.transport.edit")}
                  </Button>
                ) : null}
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}
